import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { http, formatApiError, mondayOfWeek, reportingWeek, formatWeek, lastNMondays } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Home, Clock, MapPin, Phone, Handshake, FileSignature, Target, MessageSquare, CheckCircle2, Plus, Trash2, Map as MapIcon, CalendarDays } from "lucide-react";

const emptyForm = {
  week_start: reportingWeek(),
  visites: 0,
  prospection_minutes: 0,
  prospection_type: "aucune",
  secteurs: "",
  relances_vendeurs: 0,
  relances_notes: "",
  signatures_en_cours: 0,
  signatures_notes: "",
  compromis_en_cours: 0,
  compromis_notes: "",
  objectifs_semaine_suivante: "",
  commentaires: "",
};

export default function WeeklyReportForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [newAddress, setNewAddress] = useState("");
  const [addingAddress, setAddingAddress] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(reportingWeek());

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const wk = selectedWeek;
        const [rp, pr] = await Promise.all([
          http.get("/reports/me/current", { params: { week_start: wk } }),
          http.get("/prospection/me", { params: { week_start: wk } }),
        ]);
        setForm(rp.data ? { ...emptyForm, ...rp.data, week_start: wk } : { ...emptyForm, week_start: wk });
        setAddresses(pr.data || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedWeek]);

  const addAddress = async () => {
    if (!newAddress.trim()) return;
    setAddingAddress(true);
    try {
      const { data } = await http.post("/prospection", {
        address: newAddress.trim(),
        week_start: form.week_start,
      });
      setAddresses((a) => [data, ...a]);
      setNewAddress("");
      if (data.geocoded) toast.success("Adresse ajoutée à la carte");
      else toast.warning("Adresse enregistrée, mais non localisée sur la carte");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAddingAddress(false);
    }
  };

  const removeAddress = async (id) => {
    try {
      await http.delete(`/prospection/${id}`);
      setAddresses((a) => a.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setNum = (key, value) => set(key, Math.max(0, Number(value) || 0));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await http.post("/reports", form);
      toast.success("Rapport enregistré avec succès");
      setTimeout(() => navigate("/dashboard"), 500);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto" data-testid="weekly-report-form">
        <div className="mb-6">
          <p className="label-eyebrow mb-2">Point hebdomadaire</p>
          <h1 className="font-display font-bold text-4xl tracking-tight">Rapport de la semaine</h1>
        </div>

        <Card className="p-4 mb-6 border-primary/20 bg-primary/5 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-slate-700">
            <CalendarDays className="h-4 w-4 text-primary" />
            <span className="font-semibold">Semaine remplie :</span>
          </div>
          <Select value={selectedWeek} onValueChange={setSelectedWeek}>
            <SelectTrigger className="h-10 sm:w-[280px] bg-white" data-testid="week-selector"><SelectValue /></SelectTrigger>
            <SelectContent>
              {lastNMondays(8).map((w) => (
                <SelectItem key={w} value={w}>{formatWeek(w)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-500 sm:ml-auto">
            Astuce : le lundi matin, on remplit la semaine qui vient de se terminer.
          </span>
        </Card>

        <form onSubmit={submit} className="space-y-6">
          {/* Visites */}
          <SectionCard icon={Home} title="Visites effectuées" subtitle="Combien de visites de biens sur la semaine ?">
            <NumberField
              testid="visites-input"
              value={form.visites}
              onChange={(v) => setNum("visites", v)}
              label="Nombre de visites"
            />
          </SectionCard>

          {/* Prospection */}
          <SectionCard icon={Clock} title="Prospection" subtitle="Temps passé + type et secteurs travaillés">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <NumberField
                testid="prospection-minutes-input"
                value={form.prospection_minutes}
                onChange={(v) => setNum("prospection_minutes", v)}
                label="Minutes de prospection"
                step={15}
                suffix="min"
              />
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Type</Label>
                <Select value={form.prospection_type} onValueChange={(v) => set("prospection_type", v)}>
                  <SelectTrigger className="h-11" data-testid="prospection-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aucune">Aucune</SelectItem>
                    <SelectItem value="terrain">Terrain</SelectItem>
                    <SelectItem value="telephonique">Téléphonique</SelectItem>
                    <SelectItem value="mixte">Mixte (terrain + téléphonique)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2 mt-5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Secteurs prospectés</Label>
              <Input
                data-testid="secteurs-input"
                value={form.secteurs}
                onChange={(e) => set("secteurs", e.target.value)}
                placeholder="Ex : Paris 15, Vanves, Issy-les-Moulineaux"
                className="h-11"
              />
            </div>
          </SectionCard>

          {/* Adresses prospection terrain */}
          <SectionCard icon={MapIcon} title="Adresses prospectées (terrain)" subtitle="Ajoutez chaque adresse — elles seront placées automatiquement sur la carte">
            <div className="flex gap-2">
              <Input
                data-testid="new-address-input"
                value={newAddress}
                onChange={(e) => setNewAddress(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addAddress();
                  }
                }}
                placeholder="Ex : 14 rue de Rivoli, 75001 Paris"
                className="h-11"
                disabled={addingAddress}
              />
              <Button
                type="button"
                onClick={addAddress}
                disabled={addingAddress || !newAddress.trim()}
                data-testid="add-address-button"
                className="h-11 gap-1.5 shrink-0"
              >
                {addingAddress ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Ajouter
              </Button>
            </div>
            {addresses.length > 0 ? (
              <div className="mt-4 space-y-2" data-testid="addresses-list">
                {addresses.map((a) => (
                  <div
                    key={a.id}
                    className={`flex items-center gap-3 p-2.5 rounded-md border ${
                      a.geocoded ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"
                    }`}
                    data-testid={`address-item-${a.id}`}
                  >
                    <MapIcon className={`h-4 w-4 shrink-0 ${a.geocoded ? "text-emerald-600" : "text-amber-600"}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900 truncate">{a.address}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {a.geocoded ? `📍 ${a.display_name}` : "⚠ Non localisée — vérifiez l'orthographe"}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-600 hover:bg-red-50 shrink-0"
                      onClick={() => removeAddress(a.id)}
                      data-testid={`remove-address-${a.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-4 text-xs text-slate-500 italic">
                Aucune adresse pour le moment. Ajoutez les rues, quartiers ou adresses précises que vous avez prospectés.
              </div>
            )}
          </SectionCard>

          {/* Relances vendeurs */}
          <SectionCard icon={MessageSquare} title="Relances vendeurs" subtitle="Vendeurs recontactés cette semaine">
            <NumberField
              testid="relances-input"
              value={form.relances_vendeurs}
              onChange={(v) => setNum("relances_vendeurs", v)}
              label="Nombre de relances"
            />
            <div className="space-y-2 mt-5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Notes (facultatif)</Label>
              <Textarea
                data-testid="relances-notes-input"
                value={form.relances_notes}
                onChange={(e) => set("relances_notes", e.target.value)}
                placeholder="Ex : M. Martin – prix révisé, Mme Dubois – attend décision"
                className="min-h-[80px]"
              />
            </div>
          </SectionCard>

          {/* Signatures & Compromis */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SectionCard icon={FileSignature} title="Signatures / Mandats" subtitle="Signatures en cours">
              <NumberField
                testid="signatures-input"
                value={form.signatures_en_cours}
                onChange={(v) => setNum("signatures_en_cours", v)}
                label="En cours"
              />
              <div className="space-y-2 mt-4">
                <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Notes</Label>
                <Textarea
                  data-testid="signatures-notes-input"
                  value={form.signatures_notes}
                  onChange={(e) => set("signatures_notes", e.target.value)}
                  placeholder="Détails des dossiers"
                  className="min-h-[70px]"
                />
              </div>
            </SectionCard>

            <SectionCard icon={Handshake} title="Compromis" subtitle="Compromis en cours de signature">
              <NumberField
                testid="compromis-input"
                value={form.compromis_en_cours}
                onChange={(v) => setNum("compromis_en_cours", v)}
                label="En cours"
              />
              <div className="space-y-2 mt-4">
                <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Notes</Label>
                <Textarea
                  data-testid="compromis-notes-input"
                  value={form.compromis_notes}
                  onChange={(e) => set("compromis_notes", e.target.value)}
                  placeholder="Détails / étape en cours"
                  className="min-h-[70px]"
                />
              </div>
            </SectionCard>
          </div>

          {/* Objectifs */}
          <SectionCard icon={Target} title="Objectifs semaine suivante" subtitle="Ce que je vais faire lundi prochain">
            <Textarea
              data-testid="objectifs-input"
              value={form.objectifs_semaine_suivante}
              onChange={(e) => set("objectifs_semaine_suivante", e.target.value)}
              placeholder="Ex : 5 visites, 3h de porte-à-porte secteur X, rappeler 8 vendeurs"
              className="min-h-[90px]"
            />
          </SectionCard>

          <SectionCard icon={MessageSquare} title="Commentaires libres" subtitle="Autres tâches, difficultés, informations à partager">
            <Textarea
              data-testid="commentaires-input"
              value={form.commentaires}
              onChange={(e) => set("commentaires", e.target.value)}
              placeholder="Tout ce que vous voulez transmettre à l'équipe"
              className="min-h-[100px]"
            />
          </SectionCard>

          {/* Submit */}
          <div className="sticky bottom-4 z-30">
            <Card className="p-4 flex items-center justify-between gap-4 border-2 border-primary/20 shadow-lg">
              <div className="flex items-center gap-3 text-sm">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                <span className="text-slate-700">Vos données sont sauvegardées côté serveur</span>
              </div>
              <Button
                type="submit"
                disabled={saving}
                size="lg"
                data-testid="submit-report-button"
                className="gap-2"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Enregistrer le rapport
              </Button>
            </Card>
          </div>
        </form>
      </div>
    </Layout>
  );
}

function SectionCard({ icon: Icon, title, subtitle, children }) {
  return (
    <Card className="p-6 sm:p-7">
      <div className="flex items-start gap-3 mb-5">
        <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-display font-semibold text-lg text-slate-900">{title}</h2>
          <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>
        </div>
      </div>
      {children}
    </Card>
  );
}

function NumberField({ label, value, onChange, testid, step = 1, suffix }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">{label}</Label>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-11 w-11 shrink-0"
          onClick={() => onChange(Math.max(0, value - step))}
          data-testid={`${testid}-decrement`}
        >
          –
        </Button>
        <Input
          type="number"
          min={0}
          step={step}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="h-11 text-center text-lg font-semibold"
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-11 w-11 shrink-0"
          onClick={() => onChange(value + step)}
          data-testid={`${testid}-increment`}
        >
          +
        </Button>
        {suffix ? <span className="text-sm text-slate-500 w-10">{suffix}</span> : null}
      </div>
    </div>
  );
}
